import React, { forwardRef, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { nativeSelectStyle, disabledSelectStyle } from '../sydPickerStyles'

const suffixStyle = {
  color: 'var(--color-neutral-700)',
  fontSize: 'var(--font-size-sm)',
  whiteSpace: 'nowrap'
}

const SydDurationPicker = forwardRef(function SydDurationPicker ({
  value = 60,
  onChange = noop,
  minHours = 0,
  maxHours = 8,
  step = 15,
  disabled
}, ref) {
  const hours = Math.floor(value / 60)
  const minutes = value % 60

  const hourValues = useMemo(() => {
    const vals = []
    for (let h = minHours; h <= maxHours; h++) vals.push(h)
    return vals
  }, [minHours, maxHours])

  const minuteValues = useMemo(() => {
    const vals = []
    for (let m = 0; m < 60; m += step) vals.push(m)
    return vals
  }, [step])

  const handleHourChange = useCallback((h) => {
    const m = h >= maxHours ? 0 : minutes
    onChange(h * 60 + m)
  }, [maxHours, minutes, onChange])

  const handleMinuteChange = useCallback((m) => {
    onChange(hours * 60 + m)
  }, [hours, onChange])

  const suffix = hours >= 1 ? 'hour(s)' : 'minutes'

  const atMax = hours >= maxHours

  const disabledStyle = disabled ? disabledSelectStyle : undefined

  return (
    <div ref={ref} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
      <select
        value={hours}
        onChange={(e) => handleHourChange(Number(e.target.value))}
        disabled={disabled}
        style={{ ...nativeSelectStyle, ...disabledStyle }}
      >
        {hourValues.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <span style={{ fontWeight: 600, lineHeight: 1 }}>:</span>
      <select
        value={atMax ? 0 : minutes}
        onChange={(e) => handleMinuteChange(Number(e.target.value))}
        disabled={disabled || atMax}
        style={{ ...nativeSelectStyle, ...(disabled || atMax ? disabledSelectStyle : undefined) }}
      >
        {minuteValues.map(m => (
          <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
        ))}
      </select>
      <span style={suffixStyle}>{suffix}</span>
    </div>
  )
})

SydDurationPicker.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func,
  minHours: PropTypes.number,
  maxHours: PropTypes.number,
  step: PropTypes.number,
  disabled: PropTypes.bool
}

export default SydDurationPicker
